import { useState, useEffect } from "react";
import { History, Lock, RefreshCw, Star, X } from "lucide-react";
import { HistoryItemCard } from "./HistoryItemCard";
import { API } from "../../utils/api";

/**
 * HistoryPanel Component
 * ======================
 * Slide-out panel showing script generation history with favorites support.
 */
export const HistoryPanel = ({ 
  isOpen, 
  onClose, 
  onSelectItem, 
  selectedItem, 
  user, 
  userFeatures, 
  onShowUpgradeModal 
}) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  
  const canUseFavorites = userFeatures?.can_use_favorites;

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showFavoritesOnly]);

  const fetchHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const url = showFavoritesOnly 
        ? `${API}/history?limit=30&favorites_only=true`
        : `${API}/history?limit=30`;
      const response = await fetch(url, {
        credentials: "include",
      });
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      setHistory(data.history || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleFavorite = async (historyId) => {
    try {
      const response = await fetch(`${API}/history/${historyId}/favorite`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: "include",
      });
      
      if (!response.ok) throw new Error('Failed to toggle favorite');
      
      const result = await response.json();
      
      // Update the local state
      if (showFavoritesOnly && !result.is_favorite) {
        // Remove from list if we're in favorites view and item was unfavorited
        setHistory(prev => prev.filter(item => item.history_id !== historyId));
      } else {
        setHistory(prev => prev.map(item => 
          item.history_id === historyId 
            ? { ...item, is_favorite: result.is_favorite }
            : item
        ));
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-zinc-950 border-l border-zinc-800 z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-zinc-800 rounded-lg flex items-center justify-center">
              <History className="w-4 h-4 text-zinc-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">History</h2>
              <p className="text-xs text-zinc-500">{history.length} {showFavoritesOnly ? 'favorites' : 'generations'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-history-btn"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 p-4 border-b border-zinc-800">
          <button
            onClick={() => setShowFavoritesOnly(false)}
            data-testid="filter-all-btn"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              !showFavoritesOnly
                ? 'bg-zinc-800 text-white'
                : 'bg-zinc-900/50 text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <History className="w-4 h-4" />
            All
          </button>
          <button
            onClick={() => canUseFavorites ? setShowFavoritesOnly(true) : onShowUpgradeModal?.("favorites")}
            data-testid="filter-favorites-btn"
            title={canUseFavorites ? 'View favorites' : 'Upgrade to Pro to use favorites'}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              !canUseFavorites 
                ? 'bg-zinc-900/30 text-zinc-600 hover:bg-zinc-800/50 hover:text-zinc-400 cursor-pointer'
                : showFavoritesOnly
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : 'bg-zinc-900/50 text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {canUseFavorites ? (
              <Star className={`w-4 h-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
            ) : (
              <Lock className="w-3.5 h-3.5" />
            )}
            Favorites
            {!canUseFavorites && <span className="text-[10px] ml-1 text-violet-400">PRO</span>}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-zinc-600 animate-spin mb-4" />
              <p className="text-sm text-zinc-500">Loading history...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
              {error}
            </div>
          )}

          {!isLoading && !error && history.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-14 h-14 bg-zinc-800 rounded-full flex items-center justify-center mb-4">
                {showFavoritesOnly ? (
                  <Star className="w-6 h-6 text-zinc-600" />
                ) : (
                  <History className="w-6 h-6 text-zinc-600" />
                )}
              </div>
              <p className="text-zinc-500 text-sm mb-1">
                {showFavoritesOnly ? 'No favorites yet' : 'No history yet'}
              </p>
              <p className="text-zinc-600 text-xs">
                {showFavoritesOnly 
                  ? 'Star your best scripts to find them quickly' 
                  : 'Your generated scripts will appear here'}
              </p>
            </div>
          )}

          {!isLoading && !error && history.length > 0 && (
            <div className="space-y-3">
              {history.map((item, index) => (
                <HistoryItemCard
                  key={index}
                  item={item}
                  isSelected={selectedItem === index}
                  onClick={() => onSelectItem(item, index)}
                  onToggleFavorite={handleToggleFavorite}
                  canUseFavorites={canUseFavorites}
                  onShowUpgradeModal={onShowUpgradeModal}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800">
          <p className="text-[10px] text-zinc-600 text-center">
            {showFavoritesOnly 
              ? 'Your starred scripts for quick access'
              : 'Click ⭐ to favorite • Click card to view script'}
          </p>
        </div>
      </div>
    </>
  );
};

export default HistoryPanel;
